import { useState, useCallback } from "react"
import CategorySelector from "./CategorySelector"
import { useBrandComparison } from "./hooks/useBrandComparison"
import { CustomLegend } from "./CustomLegend"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import DateRangePicker from "~/ui-rtk/components/ui/controls/DateRangePicker/DateRangePicker"
import BrandComparisonChart from "./Chart"

const dayjs = useDate()

export default function BrandComparison({
  dateRange,
  maxDate,
}: {
  dateRange: [Date, Date]
  maxDate: Date
}) {
  const { setPeriod } = usePeriod()
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [hoveredCompany, setHoveredCompany] = useState<string | null>(null)
  const [hideAICompanies, setHideAICompanies] = useState<boolean>(false)

  // Memoize callback functions to prevent unnecessary re-renders
  const changePeriod = useCallback(
    ({ start, end }: { start: Date; end: Date }) => {
      void setPeriod(
        dayjs(start).format(DATE_FORMAT),
        dayjs(end).format(DATE_FORMAT),
      )
    },
    [setPeriod],
  )

  const handleToggleAICompanies = useCallback(() => {
    setHideAICompanies(!hideAICompanies)
  }, [hideAICompanies])

  const { categories, filteredData, currentCompanyName } = useBrandComparison(
    selectedCategory,
    setSelectedCategory,
    hideAICompanies,
    dateRange,
  )

  return (
    <div className="my-system-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center justify-between">
          <h1 className="text-white text-2xl font-semibold">
            Top Brands in Category
          </h1>
        </div>

        <div className="flex items-center gap-system-sm">
          <div className="flex items-center gap-system-sm rounded-system-md p-system-sm bg-system-blue-700 h-system-2xl border border-system-blue-600">
            <Toggle
              size="xs"
              value={hideAICompanies}
              onChange={handleToggleAICompanies}
            />
            <span className="text-base text-system-gray-400">
              Hide AI Suggested Brands
            </span>
          </div>
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                comparisonEnabled={false}
                showMonthYearPicker={true}
                maxDate={maxDate}
                onChange={changePeriod}
              />
            )}
        </div>
      </div>
      <div className="w-full bg-system-blue-800 rounded-system-xl shadow-sm p-system-md border-1 border-system-blue-700 mt-system-md">
        <div className="flex flex-col space-y-system-md">
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
          <div className="h-[450px] w-full relative">
            <BrandComparisonChart
              filteredData={filteredData}
              currentCompanyName={currentCompanyName}
              hoveredCompany={hoveredCompany}
            />
          </div>
          <CustomLegend
            selectedCategory={selectedCategory}
            hoveredCompany={hoveredCompany}
            onHover={setHoveredCompany}
            data={filteredData}
            currentCompanyName={currentCompanyName}
          />
        </div>
      </div>
    </div>
  )
}
